import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CartItem, CartTotals } from '../interface/cart.interface';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiResponse } from '../interface/common.interface';
import { UserService } from './user.service';
import { tap } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class CartService {

  private cart: BehaviorSubject<CartItem[]> = new BehaviorSubject([]);
  cart$: Observable<CartItem[]> = this.cart.asObservable();

  private totals: BehaviorSubject<CartTotals> = new BehaviorSubject<CartTotals>({
    subtotal: 0,
    discount: 0,
    tax: 0,
    delivery: 0,
    total: 0
  });
  totals$: Observable<CartTotals> = this.totals.asObservable();

  private orderArray: BehaviorSubject<any[]> = new BehaviorSubject([]);
  orderArray$: Observable<any[]> = this.orderArray.asObservable();

  cartv2Api: string = environment.apiBaseUrl + 'cart-v2';

  cartData: any;
  constructor(
    private http: HttpClient,
    private userService: UserService
  ) { }

  storeCart(): void {
    const cartItems = this.cart.value;
    localStorage.setItem('sushiizu_cart', JSON.stringify(cartItems));
  }

  setCartV2Data(data: any): void {
    this.cartData = data;
    if (!data) return;

    if (data.totals) {
      this.totals.next({
        subtotal: parseFloat(data.totals.subtotal) || 0,
        discount: parseFloat(data.totals.discount) || 0,
        tax: parseFloat(data.totals.tax) || 0,
        delivery: parseFloat(data.totals.delivery) || 0,
        total: parseFloat(data.totals.total) || 0
      });
    }

    if (data.order_array && Array.isArray(data.order_array)) {
      this.orderArray.next(data.order_array);
    }

    if (data.items && Array.isArray(data.items)) {
      const mappedItems: CartItem[] = data.items.map((item: any) => ({
        ...item,
        item_id: item.item_id,
        cart_item_id: item.item_id,
        prod_id: item.product_id,
        product_id: item.product_id,
        price: item.product_price ?? item.price,
        product_price: item.product_price ?? item.price,
        line_total: item.line_total,
        total: parseFloat(item.line_total ?? item.total ?? 0),
        option: item.options ?? item.option ?? [],
        options: item.options ?? item.option ?? []
      }));
      this.cart.next(mappedItems);
    } else if (data.order_array && Array.isArray(data.order_array)) {
      this.cart.next(data.order_array);
    }
  }

  getTotals(): CartTotals {
    return this.totals.value;
  }

  getCartV2(): Observable<ApiResponse> {
    const payload = new HttpParams({
      fromObject: {
        deviceID: this.userService.getDeviceId() || '',
        session: this.userService.getSession() || '',
        operation: 'get',
        channel: 'web',
        store_id: this.userService.getStoreId() || ''
      }
    });
    return this.http.post<ApiResponse>(this.cartv2Api, payload).pipe(
      tap((response: ApiResponse) => {
        if (response && response.data) {
          this.setCartV2Data(response.data);
        }
      })
    );
  }

  getCart(): void {
    const cartItems = JSON.parse(localStorage.getItem('sushiizu_cart'));
    if (cartItems) {
      this.cart.next(cartItems);
    }
  }

  /** Synchronous: transform current cart into API-payload shape */
  getReadyCart(): any[] {
    if (this.orderArray.value && this.orderArray.value.length > 0) {
      return this.orderArray.value;
    }
    const value = this.cart.value;
    if (!Array.isArray(value)) {
      console.warn('cart.value is not an array:', value);
      return [];
    }
    return value.map(item => ({
      cart_item_id: (item as any).item_id ?? (item as any).cart_item_id ?? 0,
      prod_id: (item as any).product_id ?? item.prod_id,
      name: item.name,
      price: String((item as any).product_price ?? item.price ?? '0.00'),
      quantity: item.quantity,
      remark: item.remark ?? '',
      option: item.option ?? (item as any).options ?? []
    }));
  }

  handleCartItemv2(newItem: CartItem): Observable<ApiResponse> {
    console.log('handleCartItemv2 payload:', newItem);

    let payload = new HttpParams()
      .set('deviceID', String(this.userService.getDeviceId() ?? ''))
      .set('session', String(this.userService.getSession() ?? ''))
      .set('operation', 'add')
      .set('channel', 'web')
      .set('store_id', String(newItem.store_id ?? 0))
      .set('product_id', String(newItem.prod_id ?? newItem.product_id ?? ''))
      .set('quantity', String(newItem.quantity ?? 1));

    let optionIds = '';
    if (newItem.option_ids) {
      optionIds = newItem.option_ids;
    } else if (typeof newItem.option === 'string' && newItem.option) {
      optionIds = newItem.option;
    } else if (Array.isArray(newItem.option)) {
      optionIds = newItem.option
        .map((opt: any) => opt.option_id ?? opt.id)
        .filter((id: any) => id != null)
        .join(',');
    }

    if (optionIds) {
      payload = payload.set('option_ids', optionIds);
    }
    return this.http.post<ApiResponse>(this.cartv2Api, payload).pipe(
      tap((response: ApiResponse) => {
        if (response && response.data) {
          this.setCartV2Data(response.data);
        }
      })
    );
  }

  updateCartItemV2(itemOrId: any, quantity?: number): Observable<ApiResponse> {
    let itemId: string | number;
    let qty: number;

    if (typeof itemOrId === 'object' && itemOrId !== null) {
      itemId = itemOrId.item_id ?? itemOrId.cart_item_id ?? itemOrId.product_id ?? itemOrId.prod_id;
      qty = quantity !== undefined ? quantity : itemOrId.quantity;
    } else {
      itemId = itemOrId;
      qty = quantity ?? 1;
    }

    console.log('updateCartItemV2 item_id:', itemId, 'quantity:', qty);

    let payload = new HttpParams()
      .set('deviceID', String(this.userService.getDeviceId() ?? ''))
      .set('session', String(this.userService.getSession() ?? ''))
      .set('operation', 'update')
      .set('channel', 'web')
      .set('item_id', String(itemId ?? ''))
      .set('quantity', String(qty ?? 1));

    return this.http.post<ApiResponse>(this.cartv2Api, payload).pipe(
      tap((response: ApiResponse) => {
        if (response && response.success && response.data && (response.data.items || response.data.totals)) {
          this.setCartV2Data(response.data);
        } else {
          this.getCartV2().subscribe();
        }
      })
    );
  }

  deleteCartItemV2(itemId: number | string): Observable<ApiResponse> {
    console.log('deleteCartItemV2 item_id:', itemId);

    let payload = new HttpParams()
      .set('deviceID', String(this.userService.getDeviceId() ?? ''))
      .set('session', String(this.userService.getSession() ?? ''))
      .set('operation', 'delete')
      .set('channel', 'web')
      .set('item_id', String(itemId ?? ''));

    return this.http.post<ApiResponse>(this.cartv2Api, payload).pipe(
      tap((response: ApiResponse) => {
        if (response && response.success && response.data && (response.data.items || response.data.totals)) {
          this.setCartV2Data(response.data);
        } else {
          this.getCartV2().subscribe();
        }
      })
    );
  }

  clearCartV2(): Observable<ApiResponse> {
    console.log('clearCartV2 with operation: clear');

    let payload = new HttpParams()
      .set('deviceID', String(this.userService.getDeviceId() ?? ''))
      .set('session', String(this.userService.getSession() ?? ''))
      .set('operation', 'clear')
      .set('channel', 'web');

    return this.http.post<ApiResponse>(this.cartv2Api, payload).pipe(
      tap((response: ApiResponse) => {
        this.destroy();
        if (response && response.data && (response.data.items || response.data.totals)) {
          this.setCartV2Data(response.data);
        }
      })
    );
  }

  handleCartItem(newItem: CartItem): void {
    let found = this.findCartItem(newItem);
    if (found === -1) {
      this.addCartItem(newItem);
    } else {
      this.updateCartItem(found, newItem);
    }
    this.storeCart();
  }

  updateCartItem(index: number, newItem: CartItem): void {
    let cartItems = this.cart.value;
    cartItems[index].quantity += newItem.quantity;
    cartItems[index].total = (+cartItems[index].total || 0) + (+newItem.total || 0);
    this.cart.next(cartItems);
  }

  findCartItem(itemToFind: CartItem): number {
    let cartItems = this.cart.value;
    let foundIndex = -1;
    if (!cartItems || (cartItems && cartItems.length == 0)) {
      return foundIndex;
    }

    cartItems.find((item, index) => {
      const matchId = (item.prod_id || item.product_id) === (itemToFind.prod_id || itemToFind.product_id);
      const itemOptionLen = (item.option || item.options || []).length;
      const findOptionLen = (itemToFind.option || itemToFind.options || []).length;
      if (matchId && itemOptionLen == findOptionLen) {
        if (findOptionLen === 0) {
          foundIndex = index;
        }
        let differentOption = false;
        (item.option || []).forEach((option) => {
          let exist = (itemToFind.option || []).filter((toFindOption) => toFindOption.option_id === option.option_id);
          if (exist.length === 0) {
            differentOption = true;
          }
        });
        if (!differentOption) {
          foundIndex = index;
        }
      }
    });
    return foundIndex;
  }

  addCartItem(newItem: CartItem): void {
    let updatedCart = [...this.cart.value, newItem];
    this.cart.next(updatedCart);
  }

  removeCartItem(index: number): void {
    let updatedCart = this.cart.value;
    updatedCart.splice(index, 1);
    this.cart.next(updatedCart);
    this.storeCart();
  }

  calculateItemPrice(item: CartItem): number {
    let optionSum = 0;
    if (item && (item.option || item.options)) {
      (item.option || item.options).forEach(option => optionSum += +(option.option_price || option.price || 0));
    }
    const unitPrice = +(item.price || item.product_price || 0);
    let total = (unitPrice + optionSum) * (item.quantity || 1);
    return total;
  }

  calculateTotalCart(cart: CartItem[]): number {
    if (this.totals.value && +this.totals.value.subtotal > 0) {
      return +this.totals.value.subtotal;
    }
    let total = 0;
    if (cart && cart.length > 0) {
      cart.forEach(item => {
        const itemLineTotal = item.line_total != null ? +item.line_total : (item.total != null ? +item.total : (((+item.price || +item.product_price || 0) * (item.quantity || 1))));
        total += itemLineTotal || 0;
      });
    }
    return total;
  }

  cartLength(): number {
    const cartItems = this.cart.value;
    return cartItems.length;
  }
  cartQty(): number {
    const cartItems = this.cart.value;
    let totalQty = 0;
    cartItems.forEach(item => {
      totalQty += item.quantity || 0;
    });
    return totalQty;
  }
  cartStoreData(): {
    store_id: number,
    store_name: string
  }[] {
    const storeDetail = [
      {
        store_id: this.cartData.store_id,
        store_name: this.cartData.store_name
      }
    ];
    return storeDetail;
  }

  destroy(): void {
    this.cart.next([]);
    this.orderArray.next([]);
    this.totals.next({
      subtotal: 0,
      discount: 0,
      tax: 0,
      delivery: 0,
      total: 0
    });
    this.storeCart();
  }
}
